'use client';

import { usePathname } from 'next/navigation';
import Link from 'next/link';
import { ChevronRight, Home, Menu } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useDocsMobileNav } from '@/components/docs/docs-mobile-nav';
import { 
  Breadcrumb, 
  BreadcrumbItem, 
  BreadcrumbLink, 
  BreadcrumbList, 
  BreadcrumbSeparator 
} from '@/components/ui/breadcrumb';

export default function DocsBreadcrumb() {
  const pathname = usePathname();
  const mobileNav = useDocsMobileNav();

  if (pathname === '/docs') {
    return (
      <div className="flex items-center justify-end lg:hidden">
        <Button
          type="button"
          variant="outline"
          size="sm"
          className="shrink-0 gap-2 border-gray-300"
          onClick={() => mobileNav?.openMenu()}
        >
          <Menu className="h-4 w-4" />
          Browse docs
        </Button>
      </div>
    );
  }

  // Split the path and remove empty segments
  const segments = pathname.split('/').filter(Boolean);
  
  // Remove 'docs' from segments as it's implicit
  const breadcrumbSegments = segments.filter(segment => segment !== 'docs');
  
  // Generate breadcrumb items with proper URLs
  const breadcrumbItems = breadcrumbSegments.map((segment, index) => {
    // Build the href for this breadcrumb item
    const href = `/docs/${breadcrumbSegments.slice(0, index + 1).join('/')}`;
    
    // Format the segment for display (replace hyphens with spaces and capitalize)
    const formattedSegment = segment
      .replace(/-/g, ' ')
      .replace(/\b\w/g, char => char.toUpperCase());
    
    return {
      href,
      label: formattedSegment,
    };
  });

  return (
    <div className="flex flex-wrap items-center justify-between gap-3">
      <Breadcrumb className="min-w-0 flex-1">
        <BreadcrumbList className="flex-wrap">
          <BreadcrumbItem>
            <BreadcrumbLink asChild>
              <Link href="/docs">
                <Home className="h-4 w-4" />
              </Link>
            </BreadcrumbLink>
          </BreadcrumbItem>

          {breadcrumbItems.map((item, index) => (
            <BreadcrumbItem key={index}>
              <BreadcrumbSeparator>
                <ChevronRight className="h-4 w-4" />
              </BreadcrumbSeparator>
              <BreadcrumbLink asChild>
                <Link href={item.href}>{item.label}</Link>
              </BreadcrumbLink>
            </BreadcrumbItem>
          ))}
        </BreadcrumbList>
      </Breadcrumb>
      <Button
        type="button"
        variant="outline"
        size="sm"
        className="flex shrink-0 gap-2 border-gray-300 lg:hidden"
        onClick={() => mobileNav?.openMenu()}
      >
        <Menu className="h-4 w-4" />
        Endpoints
      </Button>
    </div>
  );
} 